import type { PlatformAdapter } from "./platform/types";
import { injectProductSchema, removeProductSchema } from "./schema";
import { onLocationChange } from "./spa-navigation";
import type { Tracker } from "./tracking";
import type { ProductContext, ScriptFeatures } from "./types";

// Produto que o DOM ainda não renderizou: 250 + 500 + 1000 + 2000 ms ≈ 4 s no total.
const RETRY_DELAYS_MS = [250, 500, 1000, 2000];
const IDLE_TIMEOUT_MS = 300;
const FALLBACK_DELAY_MS = 50;

export interface RouteHandlerOptions {
  adapter: PlatformAdapter | null;
  tracker: Tracker;
  features: Pick<ScriptFeatures, "tracking" | "schema">;
  apiBase: string;
  publicKey: string;
  connectionId: string | null;
}

export interface RouteHandler {
  // Processa a página atual agora e passa a ouvir navegações SPA (pushState/replaceState/popstate).
  start(): void;
  getProduct(): ProductContext | null;
  dispose(): void;
}

// Chave de dedupe: a URL sem hash. Mesma URL duas vezes seguidas não repete eventos nem schema.
export function normalizeUrl(href: string): string {
  const url = new URL(href);
  url.hash = "";
  return url.href;
}

// Identificadores de catálogo: produto pronto na hora.
export function hasStrongIdentifiers(product: ProductContext): boolean {
  return Boolean(product.productId || product.sku || product.gtin);
}

// Slug/slugPath vêm da URL, então só valem como fallback depois que as tentativas acabam.
export function hasSlug(product: ProductContext): boolean {
  return Boolean(product.slug || product.slugPath);
}

// Trabalho que pode esperar: idle quando o browser tem, timeout curto senão (tracking não atrasa).
export function runWhenIdle(fn: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(fn, FALLBACK_DELAY_MS);
  }
}

export function createRouteHandler(options: RouteHandlerOptions): RouteHandler {
  const { adapter, tracker, features, apiBase, publicKey, connectionId } = options;
  let lastUrl: string | null = null;
  let generation = 0;
  let currentProduct: ProductContext | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let schemaAbort: AbortController | null = null;
  let scheduled = false;
  let disposed = false;

  const cancelPending = (): void => {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
    // Aborta o fetch de schema da página anterior: a resposta não pode cair na página nova.
    schemaAbort?.abort();
    schemaAbort = null;
  };

  const activate = (product: ProductContext): void => {
    currentProduct = product;
    if (features.tracking) {
      tracker.track("product_view", { productId: product.productId, sku: product.sku });
    }
    if (features.schema) {
      schemaAbort = new AbortController();
      void injectProductSchema(apiBase, publicKey, product, connectionId, schemaAbort.signal);
    }
  };

  // Uma passada: identificador de catálogo ativa na hora. Sem ele, agenda nova tentativa (só em
  // páginas que o adapter já reconhece como produto). Esgotadas as tentativas, slug sozinho ainda
  // manda product_view/schema (fallback); sem nada, desiste em silêncio.
  const check = (gen: number, attempt: number): void => {
    if (gen !== generation || disposed) return;
    retryTimer = null;
    if (!adapter?.isProductPage()) return;

    const product = adapter.getProductContext();
    if (hasStrongIdentifiers(product)) {
      activate(product);
      return;
    }
    const delay = RETRY_DELAYS_MS[attempt];
    if (delay !== undefined) {
      retryTimer = setTimeout(() => check(gen, attempt + 1), delay);
    } else if (hasSlug(product)) {
      activate(product);
    }
  };

  const navigate = (): void => {
    if (disposed) return;
    const url = normalizeUrl(window.location.href);
    if (url === lastUrl) return;
    lastUrl = url;

    generation += 1;
    cancelPending();
    currentProduct = null;
    removeProductSchema();
    if (features.tracking) tracker.track("page_view");
    check(generation, 0);
  };

  // Hook de navegação: só compara a URL e agenda. Nada de DOM aqui, para não pesar no pushState.
  const onNavigate = (): void => {
    if (disposed || scheduled) return;
    if (normalizeUrl(window.location.href) === lastUrl) return;
    scheduled = true;
    runWhenIdle(() => {
      scheduled = false;
      navigate();
    });
  };

  return {
    start() {
      navigate();
      onLocationChange(onNavigate);
    },
    getProduct: () => currentProduct,
    dispose() {
      disposed = true;
      generation += 1;
      cancelPending();
      currentProduct = null;
    },
  };
}
